import { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import ToastPopup from '../ToastPopup/ToastPopup';
import useToast from '../../hooks/ui/useToast';
import { forgotPasswordSchema, resetPasswordSchema } from '../../validations/passwordValidation';
import styles from './PasswordForm.module.css';
import globalButtonStyles from '../../styles/ButtonGradient.module.css';

const PasswordForm = ({ type, onSubmit, loading, error }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { toast, showToast, closeToast } = useToast(3000);

  const inputFields =
    type === 'forgot'
      ? [{ type: 'email', placeholder: 'Enter your email', name: 'email' }]
      : [
          {
            type: showPassword ? 'text' : 'password',
            placeholder: 'New Password',
            name: 'password'
          },
          {
            type: showConfirmPassword ? 'text' : 'password',
            placeholder: 'Confirm Password',
            name: 'confirmPassword'
          }
        ];

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    try {
      if (type === 'forgot') {
        await forgotPasswordSchema.validateAt(name, { [name]: value });
      } else {
        await resetPasswordSchema.validateAt(name, {
          password: name === 'password' ? value : formData.password,
          confirmPassword: name === 'confirmPassword' ? value : formData.confirmPassword,
        });
      }
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (err) {
      setFormErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const validateFormData = async () => {
    try {
      if (type === 'forgot') {
        await forgotPasswordSchema.validate(formData, { abortEarly: false });
      } else {
        await resetPasswordSchema.validate(formData, { abortEarly: false });
      }
      setFormErrors({});
      return null;
    } catch (err) {
      const errors = {};
      if (err.inner?.length) {
        err.inner.forEach((e) => {
          errors[e.path] = e.message;
        });
      } else {
        errors.general = err.message;
      }
      setFormErrors(errors);
      return err.message;
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = await validateFormData();
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }
    try {
      await onSubmit(
        type === 'forgot'
          ? { email: formData.email }
          : { password: formData.password, confirmPassword: formData.confirmPassword }
      );
  
      if (error) throw new Error(error);
  
      showToast(
        type === 'coach-setup'
          ? 'Password created successfully! You can now log in.'
          : type === 'forgot'
          ? 'Reset link sent! Please check your email.'
          : 'Password reset successful!',
        'success',
        resetForm
      );
    } catch (err) {
      console.error(err);
      showToast('Something went wrong. Please try again.', 'error');
    }
  };

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit}>
      {inputFields.map((field) => (
        <div key={field.name} className={styles.inputWrapper}>
          <div className={styles.inputWithIcon}>
            <input
              type={field.type}
              placeholder={field.placeholder}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              className={styles.input}
              required
            />
            {field.name === 'password' && (type === 'reset' || type === 'coach-setup' || type === 'athlete-setup') && (
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            )}
            {field.name === 'confirmPassword' && (type === 'reset' || type === 'coach-setup' || type === 'athlete-setup') && (
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label="Toggle confirm password visibility"
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            )}
          </div>

          {field.name === 'password' &&
            (type === 'reset' || type === 'coach-setup' || type === 'athlete-setup' || type === 'forgot') &&
            !formErrors.password &&
            !formData.password && (
              <div className={styles.passwordHint}>
                Must be at least 8 characters and include an uppercase letter, lowercase letter,
                number, and special character.
              </div>
          )}

          {formErrors[field.name] && (
            <p className={styles.fieldError}>{formErrors[field.name]}</p>
          )}
        </div>
      ))}

        {error && <p className={styles.error}>{error}</p>}

        <button
          type="submit"
          className={`${globalButtonStyles.button} ${styles.submitBtn}`}
          disabled={loading}
        >
          {loading
            ? 'Processing...'
            : type === 'forgot'
            ? 'Send Reset Link'
            : type === 'coach-setup' || type === 'athlete-setup'
            ? 'Create Password'
            : 'Reset Password'}
        </button>
      </form>
      <ToastPopup
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={closeToast}
      />
    </>
  );
};

export default PasswordForm;